import React from 'react';
import { Button, Switch, Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, Spinner } from "@heroui/react";
import LogInRequiredModal from './Components/LogInRequiredModal';
import ArtworkOwnerModal from './Components/Artworks/ArtworkOwnerModal';
import ArtworkGuestModal from './Components/Artworks/ArtworkGuestModal';
import TutorialModal from './Components/TutorialModal';
import { Expand, X } from 'lucide-react';


// ===== Debug overlay for artwork chunks (pure JS) =====
export function drawGlobalChunkGrid(ctx, canvasPixelWidth, canvasPixelHeight, opts = {}) {
  const chunk = opts.chunk ?? 64;
  const zoom  = opts.zoom  ?? 1;
  const stroke = opts.stroke ?? 'rgba(180,180,180,0.5)';

  ctx.save();
  ctx.lineWidth = 1 / zoom;
  ctx.strokeStyle = stroke;
  ctx.setLineDash([6 / zoom, 6 / zoom]);

  for (let x = 0; x <= canvasPixelWidth; x += chunk) {
    ctx.beginPath();
    ctx.moveTo(x + 0.5, 0);
    ctx.lineTo(x + 0.5, canvasPixelHeight);
    ctx.stroke();
  }
  for (let y = 0; y <= canvasPixelHeight; y += chunk) {
    ctx.beginPath();
    ctx.moveTo(0, y + 0.5);
    ctx.lineTo(canvasPixelWidth, y + 0.5);
    ctx.stroke();
  }
  ctx.restore();
}

function drawDebug(ctx, {
  canvasPixelWidth,
  canvasPixelHeight,
  zoom,
  showGrid = true,
  chunk = 64,
} = {}) {
  if (showGrid) {
    drawGlobalChunkGrid(ctx, canvasPixelWidth, canvasPixelHeight, { chunk, zoom });
  }
}

// -------- Utility for this component --------
const CHUNK = 64;

const formatInt = n => Number(n ?? 0).toLocaleString('en-US');

function toChunkCoord(column1, row1) {
  const cx = Math.floor((column1 - 1) / CHUNK);
  const cy = Math.floor((row1 - 1) / CHUNK);
  return { cx, cy };
}
function chunkOrigin(cx, cy) {
  return { col1: cx * CHUNK + 1, row1: cy * CHUNK + 1 }; // 1-based origin
}

function base64ToBytes(b64) {
  if (typeof Buffer !== 'undefined') {
    return Uint8Array.from(Buffer.from(b64, 'base64'));
  }
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i) & 0xff;
  return out;
}
function base64ToU32(b64) {
  const bytes = base64ToBytes(b64);
  const buf = new ArrayBuffer(bytes.byteLength);
  new Uint8Array(buf).set(bytes);
  return new Uint32Array(buf);
}
function rleDecode(u32, total) {
  const bits = new Uint8Array(total);
  let curr = 0, idx = 0;
  for (let k = 0; k < u32.length; k++) {
    const n = u32[k] >>> 0;
    if (curr === 1) bits.fill(1, idx, idx + n);
    idx += n;
    curr ^= 1;
  }
  return bits;
}

// --- Pixel UX on top of chunks ---------------------------------------------
async function resolvePixelOccupancy(backendContext, page, x0, y0) {
  // 0-based -> 1-based
  const row1 = y0 + 1;
  const column1 = x0 + 1;

  // locate chunk
  const cx = Math.floor((column1 - 1) / CHUNK);
  const cy = Math.floor((row1 - 1) / CHUNK);

  const rows = await backendContext.fetchMaskChunksAt(page, cx, cy, /*includeEmpty*/ true);
  if (!rows || !rows.length) return { status: 'empty' };

  // find the first artwork that actually covers this pixel (one per pixel by your design)
  let artworkId = null;
  for (const r of rows) {
    const w = r.width ?? CHUNK, h = r.height ?? CHUNK;
    const total = w * h;
    const bits = rleDecode(base64ToU32(r.rle), total);
    const { col1, row1: r1 } = chunkOrigin(cx, cy);
    const i = column1 - col1;
    const j = row1 - r1;
    if (i < 0 || j < 0 || i >= w || j >= h) continue;
    if (bits[j * w + i] === 1) { artworkId = r.artworkId; break; }
  }
  if (!artworkId) return { status: 'empty' };

  // fetch the single artwork
  const art = await backendContext.fetchArtwork(artworkId);
  if (!art) return { status: 'empty' };

  // robust date compare
  const expMs = art.expiresAt instanceof Date ? art.expiresAt.getTime() : Date.parse(art.expiresAt);
  const nowMs = Date.now();

  if (Number.isFinite(expMs) && expMs > nowMs) {
    return { status: 'owned', artwork: art };
  } else {
    return { status: 'expiredOwner', artwork: art }; // <-- single 'artwork'
  }
}

function BuyPixelModal({ isOpen, onClose, onConfirm, hoursDefault = 24 }) {
  const [hours, setHours] = React.useState(hoursDefault);
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-[1000] bg-black/40 flex items-center justify-center">
      <div className="bg-slate-900 text-white p-4 rounded-lg w-80 border border-slate-700">
        <div className="text-lg font-semibold mb-2">Buy Pixel</div>
        <div className="text-sm opacity-80 mb-3">Enter number of hours:</div>
        <input
          type="number"
          min={1}
          value={hours}
          onChange={e => setHours(Math.max(1, Number(e.target.value || 1)))}
          className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1"
        />
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="px-3 py-1 rounded border border-slate-600">Cancel</button>
          <button onClick={() => onConfirm(hours)} className="px-3 py-1 rounded bg-indigo-600">Buy</button>
        </div>
      </div>
    </div>
  );
}

class ArtworkCanvas extends React.Component {
  constructor(props) {
    super(props);
    this.canvasRef = React.createRef();
    this.canvasContainerRef = React.createRef();

    // Camera/zoom
    this.scale = 2;
    this.minScale = 0.1;
    this.maxScale = 25;
    this.offsetX = 0;
    this.offsetY = 0;

    this.isMouseDown = false;
    this.downStartedHere = false;
    this.dragging = false;
    this.dragStart = { x: 0, y: 0 };
    this.DRAG_THRESHOLD = 3; // px: prevents tiny jitters from counting as drags
    this.viewportDirty = false;
    this.lastTouchAt = 0;

    this.offscreenCanvas = document.createElement('canvas');

    // Pricing
    this.BATCH_LINK_LIMIT = this.props.backendContext.userProfile?.buySize ?? 50;
    this.PRICE_PER_PIXEL_PER_HOUR = 1;

    // Brush / input state
    this.isSpaceDown = false;             // global Space modifier
    this.lastBrushPos = null;             // {x,y} in world pixels
    this._strokeDirty = false;            // whether we added pixels during drag

    this.state = {
      mouseX: null,
      mouseY: null,
      isFullScreen: false,
      logInRequired: false,

      showOwnedArtwork: false,
      ownedMasks: [],

      //Pixel
      selectedPixel: null,             // {x,y} 0-based
      selectedPixelStatus: null,       // { status: 'empty' | 'owned' | 'expiredOwner', artwork?, artworks? }
      batchMode: false,
      batchSet: new Set(),             // Set("x,y")
      batchHours: 1,
      batchCost: 0,
      showBuyBatchPanel: false,
      isBuying: false,

      // Batch result modal
      showBatchResultModal: false,
      batchResultLoading: false,
      batchResultError: null,
      batchResult: null, // { purchased:[], skipped:[], affectedArtworks:[], cost:number }

      // Artworks + mask highlighting
      highlightedArtworkId: null,
      highlightedMask: null,   // { chunks:[{cx,cy,width,height,origin,bits}], map: Map("cy,cx"->chunk) }
      highlightColor: '#00FFFF', // default cyan for active

      // Side panel
      infoPanelOpen: false,
      infoPanelLoading: false,
      selectedArtworkSummary: null,
      timeRemaining: null, // "HH:MM:SS"

      // Owner modal
      showOwnerModal: false,
      ownerModalArtworkId: null,
      // Guest modal
      showGuestModal: false,
      guestModalArtworkId: null,

      brushSize: 1, // radius in pixels (1 = single pixel)

      // Debug toggles
      showGrid: false,
    };
    //Info Panel Countdown
    this._countdownInterval = null;
    this._expiresAtMs = null;

    // Bind
    this.draw = this.draw.bind(this);
    this._ownedMaskCache = new Map();

    this._lastOpenedFromUrl = null;
  }

  initFromBackend = () => {
    const { canvas: canvasObj, canvasUrl } = this.props.backendContext || {};

    // Fallback local canvas dimensions
    this.virtualWidth  = Math.max(1, Number(canvasObj?.dimensionX ?? 512));
    this.virtualHeight = Math.max(1, Number(canvasObj?.dimensionY ?? 512));

    // Resize offscreen to new virtual dims
    this.offscreenCanvas.width = this.virtualWidth;
    this.offscreenCanvas.height = this.virtualHeight;
    this.offCtx = this.offscreenCanvas.getContext('2d', { willReadFrequently: true });

    // Clear to white
    this.offCtx.imageSmoothingEnabled = false;
    this.offCtx.fillStyle = 'white';
    this.offCtx.fillRect(0, 0, this.virtualWidth, this.virtualHeight);

    // Load snapshot if present (S3 URL first, then inline base64)
    if (canvasUrl) {
      this.loadSnapshotImage(canvasUrl);
    } else if (canvasObj?.imageData) {
      this.loadSnapshotImage(`data:image/png;base64,${canvasObj.imageData}`);
    } else {
      // nothing to load; just render a blank canvas
      this.draw();
    }

    // Reset zoom/offset the first time after re-init
    const canvas = this.canvasRef.current;
    if (canvas) {
      const visibleWidth = canvas.width;
      const visibleHeight = canvas.height;
      const minVisibleScale = Math.max(visibleWidth / this.virtualWidth, visibleHeight / this.virtualHeight);
      this.scale = Math.max(minVisibleScale, Math.min(this.scale, this.maxScale));
      this.offsetX = 0;
      this.offsetY = 0;
    }

    // Clear highlights/selection relevant to previous page
    this._ownedMaskCache?.clear?.();
    this.setState({
      highlightedArtworkId: null,
      highlightedMask: null,
      selectedPixel: null,
      selectedPixelStatus: null,
      showOwnerModal: false,
      ownerModalArtworkId: null,
      showGuestModal: false,
      guestModalArtworkId: null,
    }, this.draw);
  };

  bindCanvasListeners = () => {
    const canvas = this.canvasRef.current;
    if (!canvas || this._canvasBound === canvas) return;

    // Unbind previous (if any)
    if (this._canvasBound && this._canvasBound !== canvas) {
      try {
        this._canvasBound.removeEventListener('mousedown', this.onMouseDown);
        this._canvasBound.removeEventListener('mouseup', this.onMouseUp);
        this._canvasBound.removeEventListener('mouseleave', this.onMouseUp);
        this._canvasBound.removeEventListener('mousemove', this.onMouseMove);
        this._canvasBound.removeEventListener('touchstart', this.onTouchStart);
        this._canvasBound.removeEventListener('touchmove', this.onTouchMove);
        this._canvasBound.removeEventListener('touchend', this.onTouchEnd);
        this._canvasBound.removeEventListener('touchcancel', this.onTouchCancel);
        this._canvasBound.removeEventListener('wheel', this.onWheel);
        this._canvasBound.removeEventListener('contextmenu', this.onContextMenu);
        window.removeEventListener('resize', this.resizeCanvas);
      } catch {}
    }

    // Bind to the fresh node
    canvas.addEventListener('mousedown', this.onMouseDown);
    canvas.addEventListener('mouseup', this.onMouseUp);
    canvas.addEventListener('mouseleave', this.onMouseUp);
    canvas.addEventListener('mousemove', this.onMouseMove);
    canvas.addEventListener('touchstart', this.onTouchStart, { passive: true });
    canvas.addEventListener('touchmove', this.onTouchMove, { passive: false });
    canvas.addEventListener('touchend', this.onTouchEnd, { passive: true });
    canvas.addEventListener('touchcancel', this.onTouchCancel, { passive: true });
    canvas.addEventListener('wheel', this.onWheel, { passive: false });
    canvas.addEventListener('contextmenu', this.onContextMenu);
    window.addEventListener('resize', this.resizeCanvas);

    this._canvasBound = canvas;
    this.resizeCanvas();   // <— critical: size the new canvas
  };

  async componentDidMount() {
    const { backendContext } = this.props;
    this.initFromBackend();

    this.unsubscribeArtwork = await backendContext.subscribeToCanvasArtworkUpdates();
    this.unsubscribeChunk   = await backendContext.subscribeToCanvasArtworkMaskChunkUpdates();

    this.unsubscribeCanvasUpdatesLocal = backendContext.subscribeToCanvasUpdatesLocal(async (evt) => {
      try {
        const kind = evt?.kind;
        const rec  = evt?.record ?? evt;

        if (!rec) return;

        if (kind === 'Canvas') {
          // Full-page snapshot update only here:
          if (rec.imageData) this.loadSnapshotImage(rec.imageData);
          return;
        }

        if (kind === 'Artwork') {
          // Only patch if it’s the same page and has an image to apply
          if (rec.page !== this.props.page || !rec.id) return;
          // imageData may be null when only metadata changed; skip if missing
          if (!rec.imageData) return;
          await this.applyArtworkPatchToOffscreen(rec.id, rec);
          this.draw();
          return;
        }

        if (kind === 'ArtworkMaskChunk') {
          if (rec.page !== this.props.page || !rec.artworkId) return;

          // If this artwork is owned, refresh its cached mask
          if (this.state.showOwnedArtwork) {
            const ownedIds = new Set(this.getOwnedArtworkIdsForCurrentPage());
            if (ownedIds.has(rec.artworkId)) {
              const chunks = await this.props.backendContext.fetchArtworkSilhouetteChunks(rec.artworkId);
              const packed = this.packChunks(chunks || []);
              this._ownedMaskCache.set(rec.artworkId, packed);

              // Update ownedMasks array in state
              const next = (this.state.ownedMasks || []).map(m =>
                m.artworkId === rec.artworkId ? { artworkId: rec.artworkId, ...packed } : m
              );
              this.setState({ ownedMasks: next }, this.draw);
            }
          }

          // Optional: if artwork has imageData, you already re-blit it below
          const art = await this.props.backendContext.fetchArtwork(rec.artworkId);
          if (art?.imageData) {
            await this.applyArtworkPatchToOffscreen(rec.artworkId, art);
            this.draw();
          }
          return;
        }
      } catch (e) {
        console.error('canvas subscriber error:', e);
      }
    });

    const canvas = this.canvasRef.current;
    if (!canvas) return;

    window.addEventListener('keydown', this.onKeyDown, { passive: false });
    window.addEventListener('keyup', this.onKeyUp);

    window.addEventListener("resize", this.resizeCanvas);
    this.resizeCanvas();

    canvas.addEventListener('mousedown', this.onMouseDown);
    canvas.addEventListener('mouseup', this.onMouseUp);
    canvas.addEventListener('mouseleave', this.onMouseUp);
    canvas.addEventListener('mousemove', this.onMouseMove);
    canvas.addEventListener('touchstart', this.onTouchStart, { passive: true });
    canvas.addEventListener('touchmove', this.onTouchMove, { passive: false });
    canvas.addEventListener('touchend', this.onTouchEnd, { passive: true });
    canvas.addEventListener('touchcancel', this.onTouchCancel, { passive: true });
    canvas.addEventListener('wheel', this.onWheel, { passive: false });
    canvas.addEventListener('contextmenu', this.onContextMenu);

    this.draw();

    // Auto-open from deep link on first mount
    if (this.props.selectedArtworkId) {
      this.tryOpenDeepLinkedArtwork(this.props.selectedArtworkId);
    }
  }

  componentWillUnmount() {
    try { this.unsubscribeArtwork?.(); } catch {}
    try { this.unsubscribeChunk?.(); } catch {}
    try { this.unsubscribeCanvasUpdatesLocal?.(); } catch {}
    const canvas = this.canvasRef.current;
    if (!canvas) return;


    this.stopCountdown();
    canvas.removeEventListener('mousedown', this.onMouseDown);
    canvas.removeEventListener('mouseup', this.onMouseUp);
    canvas.removeEventListener('mouseleave', this.onMouseUp);
    canvas.removeEventListener('mousemove', this.onMouseMove);
    canvas.removeEventListener('touchstart', this.onTouchStart);
    canvas.removeEventListener('touchmove', this.onTouchMove);
    canvas.removeEventListener('touchend', this.onTouchEnd);
    canvas.removeEventListener('touchcancel', this.onTouchCancel);
    canvas.removeEventListener('wheel', this.onWheel);
    canvas.removeEventListener('contextmenu', this.onContextMenu);
    window.removeEventListener('resize', this.resizeCanvas);

    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
  }

  componentDidUpdate(prevProps, prevState) {
    // Respond to changes in the deep-linked artwork id
    if (prevProps.selectedArtworkId !== this.props.selectedArtworkId) {
      if (this.props.selectedArtworkId) {
        this.tryOpenDeepLinkedArtwork(this.props.selectedArtworkId);
      } else {
        // If URL removed the id and modal is open, close both
        if (this.state.showOwnerModal || this.state.showGuestModal) {
          this.setState({
            showOwnerModal: false,
            ownerModalArtworkId: null,
            showGuestModal: false,
            guestModalArtworkId: null,
          });
        }
      }
    }

    if (prevProps.backendContext.isLoading && !this.props.backendContext.isLoading) {
      const canvas = this.canvasRef.current;
      if (!canvas) return;

      if (!this._hasInitializedZoom) {
        const visibleWidth = canvas.width;
        const visibleHeight = canvas.height;
        const randomX = Math.random() * this.virtualWidth;
        const randomY = Math.random() * this.virtualHeight;

        this.offsetX = Math.max(0, Math.min(this.virtualWidth - visibleWidth / this.scale, randomX - visibleWidth / (2 * this.scale)));
        this.offsetY = Math.max(0, Math.min(this.virtualHeight - visibleHeight / this.scale, randomY - visibleHeight / (2 * this.scale)));
        this._hasInitializedZoom = true;
      }

      if (this.state.showOwnedArtwork &&
          prevProps.backendContext.userArtwork !== this.props.backendContext.userArtwork) {
        this.preloadOwnedArtworkMasks();
      }
      this.draw();
    }

    const loadingFinished = prevProps.backendContext.isLoading && !this.props.backendContext.isLoading;

    const prevPage = prevProps.page;
    const nextPage = this.props.page;

    const prevCanvasPage = prevProps.backendContext?.canvas?.page;
    const nextCanvasPage = this.props.backendContext?.canvas?.page;

    const prevUrl = prevProps.backendContext?.canvasUrl;
    const nextUrl = this.props.backendContext?.canvasUrl;

    const inputsChanged =
      prevPage !== nextPage ||
      prevCanvasPage !== nextCanvasPage ||
      prevUrl !== nextUrl;

    // Re-init if page changed, canvas changed to a different page, or the snapshot URL changed
    if (loadingFinished || inputsChanged) {
      this.initFromBackend();     // re-size offscreen + (re)load snapshot
      this.bindCanvasListeners(); // attach to the *new* canvas element
    }

    const prevStatus = prevState?.selectedPixelStatus?.status;
    const nextStatus = this.state?.selectedPixelStatus?.status;

    const prevExpires = prevState?.selectedPixelStatus?.artwork?.expiresAt;
    const nextExpires = this.state?.selectedPixelStatus?.artwork?.expiresAt;

    // Restart/stop when status or expiresAt changes
    if (prevStatus !== nextStatus || prevExpires !== nextExpires) {
      if (nextStatus === 'owned' && nextExpires) {
        this.startCountdown(nextExpires);
      } else {
        this.stopCountdown();
        if (nextStatus !== 'owned') {
          // Clear any previous value so UI shows "—" for non-owned states
          if (this.state.timeRemaining !== null) {
            this.setState({ timeRemaining: null });
          }
        }
      }
    }
  }

  // -------------------- sizing --------------------
  clampOffsets = (nextOffsetX, nextOffsetY, scale = this.scale) => {
    const canvas = this.canvasRef.current;
    if (!canvas) {
      return { offsetX: nextOffsetX, offsetY: nextOffsetY };
    }

    const visibleCanvasWidth = canvas.width / scale;
    const visibleCanvasHeight = canvas.height / scale;

    const minOffsetX = visibleCanvasWidth >= this.virtualWidth
      ? (this.virtualWidth - visibleCanvasWidth) / 2
      : 0;
    const maxOffsetX = visibleCanvasWidth >= this.virtualWidth
      ? minOffsetX
      : this.virtualWidth - visibleCanvasWidth;

    const minOffsetY = visibleCanvasHeight >= this.virtualHeight
      ? (this.virtualHeight - visibleCanvasHeight) / 2
      : 0;
    const maxOffsetY = visibleCanvasHeight >= this.virtualHeight
      ? minOffsetY
      : this.virtualHeight - visibleCanvasHeight;

    return {
      offsetX: Math.min(Math.max(nextOffsetX, minOffsetX), maxOffsetX),
      offsetY: Math.min(Math.max(nextOffsetY, minOffsetY), maxOffsetY),
    };
  };

  fitCanvasToViewport = () => {
    const canvas = this.canvasRef.current;
    if (!canvas || !this.virtualWidth || !this.virtualHeight) return;

    const visibleWidth = canvas.width;
    const visibleHeight = canvas.height;
    if (!visibleWidth || !visibleHeight) return;

    const fitScale = Math.max(visibleWidth / this.virtualWidth, visibleHeight / this.virtualHeight);
    this.scale = Math.min(this.maxScale, Math.max(this.minScale, fitScale));

    const visibleCanvasWidth = visibleWidth / this.scale;
    const visibleCanvasHeight = visibleHeight / this.scale;
    this.offsetX = Math.max(0, (this.virtualWidth - visibleCanvasWidth) / 2);
    this.offsetY = Math.max(0, (this.virtualHeight - visibleCanvasHeight) / 2);
  };

  resizeCanvas = () => {
    const canvas = this.canvasRef.current;
    const container = this.canvasContainerRef.current;
    if (canvas && container) {
      const rect = container.getBoundingClientRect();
      canvas.width  = rect.width;
      canvas.height = rect.height;
      if (!this.viewportDirty) {
        this.fitCanvasToViewport();
      }
      this.draw();
    }
  };

  // -------------------- coords --------------------
  getMouseCanvasPixel(e) {
    const canvas = this.canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = Math.floor((e.clientX - rect.left) / this.scale + this.offsetX);
    const y = Math.floor((e.clientY - rect.top)  / this.scale + this.offsetY);
    return { x, y };
  }

  getClientCanvasPixel(clientX, clientY) {
    const canvas = this.canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = Math.floor((clientX - rect.left) / this.scale + this.offsetX);
    const y = Math.floor((clientY - rect.top) / this.scale + this.offsetY);
    return { x, y };
  }

  // -------------------- snapshot --------------------
  loadSnapshotImage(url) {
    if (!url) return;
    const img = new Image();
    img.crossOrigin = 'anonymous';        // <— important
    // img.referrerPolicy = 'no-referrer'; // optional, helps with some CDNs
    img.onload = () => {
      this.offCtx.clearRect(0, 0, this.virtualWidth, this.virtualHeight);
      this.offCtx.drawImage(img, 0, 0);
      this.draw();
    };
    img.onerror = (e) => console.error('snapshot load failed', e);
    img.src = url;
  }

  

  // -------------------- canvas Subscrite --------------------
  applyArtworkPatchToOffscreen = async (artworkId, artworkRow /* {imageData, imageOriginColumn?, imageOriginRow?, imageWidth?, imageHeight?} */) => {
    if (!artworkRow?.imageData) return;

    // 1) fetch the mask chunks for this artwork
    const chunks = await this.props.backendContext.fetchArtworkSilhouetteChunks(artworkId);

    // 2) build bbox and a lightweight list of filled cells by chunk (no giant Set needed)
    let minCol = Infinity, minRow = Infinity, maxCol = -Infinity, maxRow = -Infinity;
    const decoded = []; // {cx,cy,width,height,origin,bits}

    for (const c of (chunks || [])) {
      const cx = c.chunkX ?? c.cx;
      const cy = c.chunkY ?? c.cy;
      const width  = c.width  ?? CHUNK;
      const height = c.height ?? CHUNK;
      const total  = width * height;
      const bits   = rleDecode(base64ToU32(c.rle), total);
      const origin = chunkOrigin(cx, cy);

      decoded.push({ cx, cy, width, height, origin, bits });

      // bbox from bits (faster to approximate from chunk bounds, but this is exact)
      for (let j=0;j<height;j++){
        const rowOff = j*width;
        for (let i=0;i<width;i++){
          if (!bits[rowOff+i]) continue;
          const col = origin.col1 + i;
          const row = origin.row1 + j;
          if (col < minCol) minCol = col;
          if (row < minRow) minRow = row;
          if (col > maxCol) maxCol = col;
          if (row > maxRow) maxRow = row;
        }
      }
    }

    if (!Number.isFinite(minCol)) return; // nothing to draw

    const bboxW = maxCol - minCol + 1;
    const bboxH = maxRow - minRow + 1;

    // 3) draw into a temp canvas (pixel-accurate, world coords)
    const temp = document.createElement('canvas');
    temp.width = bboxW;
    temp.height = bboxH;
    const tctx = temp.getContext('2d', { willReadFrequently: true });
    tctx.imageSmoothingEnabled = false;

    // a) paint mask as white rects
    tctx.fillStyle = 'white';
    for (const ch of decoded) {
      const baseX = ch.origin.col1 - minCol; // local to bbox
      const baseY = ch.origin.row1 - minRow;
      const { width, height, bits } = ch;
      for (let j=0;j<height;j++){
        const rowOff = j*width;
        for (let i=0;i<width;i++){
          if (!bits[rowOff+i]) continue;
          tctx.fillRect(baseX + i, baseY + j, 1, 1);
        }
      }
    }

    // b) draw artwork image clipped to mask
    await new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const originCol = artworkRow.imageOriginColumn ?? minCol; // 1-based
        const originRow = artworkRow.imageOriginRow   ?? minRow;
        const imgW = artworkRow.imageWidth  ?? img.width;
        const imgH = artworkRow.imageHeight ?? img.height;

        const dx = (originCol - minCol);
        const dy = (originRow - minRow);

        tctx.save();
        tctx.globalCompositeOperation = 'source-atop';
        tctx.drawImage(img, 0, 0, imgW, imgH, dx, dy, imgW, imgH);
        tctx.restore();
        resolve();
      };
      img.onerror = resolve;
      img.src = `data:image/png;base64,${artworkRow.imageData}`;
    });

    // 4) blit temp into the world offscreen canvas at (minCol-1, minRow-1)
    const worldX = minCol - 1; // convert 1-based to 0-based world coords
    const worldY = minRow - 1;
    this.offCtx.imageSmoothingEnabled = false;
    this.offCtx.drawImage(temp, worldX, worldY);
  };

  // ====================== ARTWORK / CHUNKS ======================
  // Decode + pack a chunk list into {chunks[], map}
  packChunks(chunks) {
    const out = [];
    const map = new Map();
    for (const c of chunks) {
      const cx = c.chunkX ?? c.cx;
      const cy = c.chunkY ?? c.cy;
      const width = c.width ?? CHUNK;
      const height = c.height ?? CHUNK;
      const total = width * height;
      const rleU32 = base64ToU32(c.rle);
      const bits = rleDecode(rleU32, total);
      const origin = chunkOrigin(cx, cy);
      const packed = { cx, cy, width, height, origin, bits, count: c.count ?? undefined };
      out.push(packed);
      map.set(`${cy},${cx}`, packed);
    }
    return { chunks: out, map };
  }

  // Compute bbox directly from chunk bits
  computeArtworkBBoxFromChunks(chunks) {
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const ch of chunks) {
      const { origin, width, height, bits } = ch;
      const baseX = origin.col1 - 1;
      const baseY = origin.row1 - 1;
      for (let j = 0; j < height; j++) {
        const rowOff = j * width;
        for (let i = 0; i < width; i++) {
          if (!bits[rowOff + i]) continue;
          const x = baseX + i;
          const y = baseY + j;
          if (x < minX) minX = x;
          if (y < minY) minY = y;
          if (x > maxX) maxX = x;
          if (y > maxY) maxY = y;
        }
      }
    }
    if (!Number.isFinite(minX)) return null;
    return { x: minX, y: minY, width: maxX - minX + 1, height: maxY - minY + 1 };
  }

  // Highlight an artwork by id (using chunks)
  highlightArtwork = async (artworkId, color = '#00FFFF') => {
    try {
      const chunks = await this.props.backendContext.fetchArtworkSilhouetteChunks(artworkId);
      if (!Array.isArray(chunks) || chunks.length === 0) return;
      const packed = this.packChunks(chunks);

      this.setState({
        highlightedArtworkId: artworkId,
        highlightedMask: packed,
        highlightColor: color,
        infoPanelOpen: true,
        infoPanelLoading: true,
        selectedArtworkSummary: null,
      }, async () => {
        try {
          const artworkSummary = (this.props.backendContext.userArtwork || []).find(a => a?.id === artworkId) || null;
          this.setState({ selectedArtworkSummary: artworkSummary ?? null, infoPanelLoading: false }, this.draw);
        } catch (e) {
          console.error('fetchArtwork failed:', e);
          this.setState({ infoPanelLoading: false }, this.draw);
        }
      });

      this.draw();
    } catch (e) {
      console.error('highlightArtwork failed:', e);
    }
  };

  // Get artworks owned by current user on this page
  getOwnedArtworkIdsForCurrentPage = () => {
    const list = this.props.backendContext.userArtwork || []; // array of Artwork rows
    return list
      .filter(a => a?.page === this.props.page && a?.id)
      .map(a => a.id);
  };

  preloadOwnedArtworkMasks = async () => {
    const ids = this.getOwnedArtworkIdsForCurrentPage();

    const cachedList = this.props.backendContext.userArtwork || [];

    const ownedMasks = [];

    for (const id of ids) {
      let packed = this._ownedMaskCache.get(id);
      if (!packed) {
        const chunks = await this.props.backendContext.fetchArtworkSilhouetteChunks(id);
        packed = this.packChunks(chunks || []);
        this._ownedMaskCache.set(id, packed);
      }
      // pull artwork row so we have expiresAt + size
      const art = cachedList.find(a => a?.id === id) || null;

      const sizeFromMask = packed.chunks.reduce((s, ch) => s + (ch.count ?? 0), 0);

      ownedMasks.push({
        artworkId: id,
        ...packed,
        expiresAt: art?.expiresAt ?? null,
        size: (art?.size ?? sizeFromMask) || 0,
        ownerSub: art?.ownerSub ?? null,
      });
    }
    this.setState({ ownedMasks }, this.draw);
  };

  // Find artwork under a click by reading mask chunks at that chunk coordinate
  findArtworkAt = async (page, x0, y0) => {
    // x0,y0 are 0-based; convert to 1-based pixel coords for chunk math
    const row1 = y0 + 1;
    const column1 = x0 + 1;
    const { cx, cy } = toChunkCoord(column1, row1);

    // You need a backend method that returns all mask rows at (page,cx,cy)
    const rows = await this.props.backendContext.fetchMaskChunksAt(page, cx, cy);
    if (!rows || !rows.length) return null;

    for (const r of rows) {
      const w = r.width ?? CHUNK;
      const h = r.height ?? CHUNK;
      const total = w * h;
      const bits = rleDecode(base64ToU32(r.rle), total);
      const { col1, row1: r1 } = chunkOrigin(cx, cy);
      const i = column1 - col1;
      const j = row1 - r1;
      if (i < 0 || j < 0 || i >= w || j >= h) continue;
      if (bits[j * w + i] === 1) return r.artworkId;
    }
    return null;
  };

  drawSelectionCorners(ctx, x, y) {
    const L = 0.25; // corner length in "pixel units"
    ctx.save();
    ctx.lineWidth = 1 / this.scale;
    ctx.strokeStyle = '#000000';
    // top-left
    ctx.beginPath(); ctx.moveTo(x, y+L); ctx.lineTo(x, y); ctx.lineTo(x+L, y); ctx.stroke();
    // top-right
    ctx.beginPath(); ctx.moveTo(x+1-L, y); ctx.lineTo(x+1, y); ctx.lineTo(x+1, y+L); ctx.stroke();
    // bottom-right
    ctx.beginPath(); ctx.moveTo(x+1, y+1-L); ctx.lineTo(x+1, y+1); ctx.lineTo(x+1-L, y+1); ctx.stroke();
    // bottom-left
    ctx.beginPath(); ctx.moveTo(x+L, y+1); ctx.lineTo(x, y+1); ctx.lineTo(x, y+1-L); ctx.stroke();
    ctx.restore();
  }

  // Crop offscreen to base64 (for editor seed)
  cropOffscreenToBase64({ x, y, width, height }) {
    const tmp = document.createElement('canvas');
    tmp.width = width;
    tmp.height = height;
    const tctx = tmp.getContext('2d', { willReadFrequently: true });
    tctx.imageSmoothingEnabled = false;
    tctx.drawImage(this.offscreenCanvas, x, y, width, height, 0, 0, width, height);
    return tmp.toDataURL('image/png');
  }

  // Called by OwnerArtworkModal when user clicks “Edit”
  requestArtworkSeed = async (artworkId) => {
    const chunks = await this.props.backendContext.fetchArtworkSilhouetteChunks(artworkId);
    const packed = this.packChunks(chunks);
    const bbox = this.computeArtworkBBoxFromChunks(packed.chunks);
    if (!bbox) return null;
    const seedImage = this.cropOffscreenToBase64(bbox);
    // mimic your previous API
    const artwork = await this.props.backendContext.fetchArtwork(artworkId);
    return { artwork, bbox, seedImage };
  };

  // Called by OwnerArtworkModal after save
  applyPatchFromModal = async ({ base64Image, bbox }) => {
    try {
      await new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
          this.offCtx.imageSmoothingEnabled = false;
          this.offCtx.drawImage(img, bbox.x, bbox.y);
          this.draw();
          resolve();
        };
        img.onerror = reject;
        img.src = /^data:/.test(base64Image) ? base64Image : `data:image/png;base64,${base64Image}`;
      });
      // persist snapshot
      const dataUrl = this.offscreenCanvas.toDataURL('image/png');
      //const base64 = dataUrl.split(',')[1];
      //await this.props.backendContext.updateCanvasSnapshot(base64, this.props.page);

      const blob = await (await fetch(dataUrl)).blob();
      await this.props.backendContext.updateCanvasSnapshotS3(blob, this.props.page);
    } catch (e) {
      console.error('applyPatchFromModal failed', e);
    }
  };

  tryOpenDeepLinkedArtwork = async (artworkId) => {
    if (!artworkId || artworkId === this._lastOpenedFromUrl) return;
    try {
      // Reuse your open logic (owner/guest detection happens inside)
      await this.openArtworkModal(artworkId);
      this._lastOpenedFromUrl = artworkId;
    } catch (e) {
      console.error('Failed to open deep-linked artwork:', e);
    }
  };

  openArtworkModal = async (artworkId, fromDeepLink = false) => {
    try {
      const userSub = this.props.backendContext?.userProfile?.sub || null;

      // ensure we have ownerSub (fetch if needed)
      let art = null;
      try {
        art = await this.props.backendContext.fetchArtwork(artworkId);
      } catch (e) {
        console.error('fetchArtwork failed in openArtworkModal:', e);
      }
      const ownerSub = art?.ownerSub || null;

      const isOwner = userSub && ownerSub && userSub === ownerSub;

      if (isOwner) {
        this.setState({
          ownerModalArtworkId: artworkId,
          showOwnerModal: true,
          // ensure guest modal isn't open at the same time
          showGuestModal: false,
          selectedPixel: null,
          selectedPixelStatus: null,
        });
      } else {
        this.setState({
          guestModalArtworkId: artworkId,
          showGuestModal: true,
          showOwnerModal: false,
          selectedPixel: null,
          selectedPixelStatus: null,
        });
      }
      // Only push to URL if this was a user action from within the app.
      if (!fromDeepLink) this.props.onOpenArtwork?.(artworkId);
    } catch (e) {
      console.error('openArtworkModal failed:', e);
      // fallback: open guest modal
      this.setState({
        guestModalArtworkId: artworkId,
        showGuestModal: true,
        showOwnerModal: false,
      });
      if (!fromDeepLink) this.props.onOpenArtwork?.(artworkId);
    }
  };

  // -------------------- interactions --------------------
  // helper
  isInsideCanvas = (e) => {
    const canvas = this.canvasRef.current;
    const r = canvas.getBoundingClientRect();
    return e.clientX >= r.left && e.clientX < r.right && e.clientY >= r.top && e.clientY < r.bottom;
  };

  isInsideClientPoint = (clientX, clientY) => {
    const canvas = this.canvasRef.current;
    const r = canvas.getBoundingClientRect();
    return clientX >= r.left && clientX < r.right && clientY >= r.top && clientY < r.bottom;
  };

  isSyntheticMouseEvent = () => Date.now() - this.lastTouchAt < 500;

  handleCanvasSelection = async (x, y) => {
    if (this.state.batchMode) {
      this.toggleBatchPixel(x, y);
      return;
    }

    this.setState({ selectedPixel: { x, y }, selectedPixelStatus: null }, this.draw);

    try {
      const status = await resolvePixelOccupancy(this.props.backendContext, this.props.page, x, y);
      this.setState({ selectedPixelStatus: status });

      if (status.status === 'owned' && status.artwork?.id) {
        await this.highlightArtwork(status.artwork.id, '#00FFFF');
      } else if (status.status === 'expiredOwner' && status.artwork?.id) {
        await this.highlightArtwork(status.artwork.id, '#ef4444');
      } else {
        this.setState({ highlightedArtworkId: null, highlightedMask: null, infoPanelOpen: false }, this.draw);
      }
    } catch (err) {
      console.error('pixel status failed:', err);
    }
  };

  onMouseDown = (e) => {
    if (this.isSyntheticMouseEvent()) return;
    if (e.button !== 0) return;       // left only
    if (!this.isInsideCanvas(e)) return;

    this.isMouseDown = true;
    this.downStartedHere = true;
    this._strokeDirty = false;

    const { x, y } = this.getMouseCanvasPixel(e);

    // If Space is held and we're in batch mode, begin brush stroke immediately
    if (this.isSpaceDown && this.state.batchMode) {
      this.lastBrushPos = { x, y };
      if (!this.props.backendContext.userProfile) {
        this.setState({ logInRequired: true });
        return;
      }
      // ensure masks available for adjacency-cost (already done when entering batch mode)
      // add at down point
      if (this.addBrushAt(x, y)) {
        this._strokeDirty = true;
        this.draw();
      }
      return;
    }

    // else: normal drag (pan/select)
    this.downStartedHere = true;
    this.dragging = false;
    this.dragStart.x = e.clientX;
    this.dragStart.y = e.clientY;
  };

  onMouseUp = async (e) => {
    if (this.isSyntheticMouseEvent()) return;
    if (e.button !== 0) return;

    // HARD GUARD: ignore any stray ups/clicks that did not start on the canvas
    if (!this.downStartedHere) return;

    // Finish brush stroke
    if (this.isSpaceDown && this.state.batchMode) {
      this.isMouseDown = false;
      this.lastBrushPos = null;
      this.downStartedHere = false;
      // recompute cost once per stroke for performance
      if (this._strokeDirty) {
        const newCost = await this.estimateBatchCost(this.state.batchSet, this.state.batchHours);
        this.setState({ batchCost: newCost });
        this._strokeDirty = false;
      }
      return;
    }

    const wasDragging = this.dragging;
    const inside = this.isInsideCanvas(e);

    this.isMouseDown = false;
    this.dragging = false;
    this.downStartedHere = false;

    // If we dragged or released outside, don't select
    if (wasDragging || !inside) return;

    // Selection (mouseup-as-click)
    const { x, y } = this.getMouseCanvasPixel(e);
    if (x < 0 || y < 0 || x >= this.virtualWidth || y >= this.virtualHeight) return;

    await this.handleCanvasSelection(x, y);
  };

  onMouseMove = (e) => {
    if (this.isSyntheticMouseEvent()) return;
    const inside = this.isInsideCanvas(e);

    // Update hover only when inside; keep previous when outside
    if (inside) {
      const { x, y } = this.getMouseCanvasPixel(e);
      this.setState({
        mouseX: x >= 0 && x < this.virtualWidth  ? x + 1 : this.state.mouseX,
        mouseY: y >= 0 && y < this.virtualHeight ? y + 1 : this.state.mouseY,
      });
    }

    if (!this.isMouseDown) return;

    // Brush stroke (Space + batchMode)
    if (this.isSpaceDown && this.state.batchMode) {
      const { x, y } = this.getMouseCanvasPixel(e);
      if (this.lastBrushPos) {
        if (this.addBrushStroke(this.lastBrushPos.x, this.lastBrushPos.y, x, y)) {
          this._strokeDirty = true;
          this.draw(); // visualize immediately; cost will update on mouseup
        }
      }
      this.lastBrushPos = { x, y };
      return; // don't pan while brushing
    }

    // Promote to dragging after threshold
    const dxClient = e.clientX - this.dragStart.x;
    const dyClient = e.clientY - this.dragStart.y;
    if (!this.dragging) {
      const moved2 = dxClient * dxClient + dyClient * dyClient;
      if (moved2 < this.DRAG_THRESHOLD * this.DRAG_THRESHOLD) return;
      this.dragging = true;
      this.viewportDirty = true;
    }

    // Pan even when outside
    const canvas = this.canvasRef.current;
    const dx = dxClient / this.scale;
    const dy = dyClient / this.scale;

    this.offsetX = Math.min(
      Math.max(this.offsetX - dx, 0),
      this.virtualWidth - canvas.width / this.scale
    );
    this.offsetY = Math.min(
      Math.max(this.offsetY - dy, 0),
      this.virtualHeight - canvas.height / this.scale
    );

    this.dragStart.x = e.clientX;
    this.dragStart.y = e.clientY;
    this.draw();
  };

  onMouseLeave = () => {
    if (this.isMouseDown) {
      this.isMouseDown = false;
      this.dragging = false;
      this.downStartedHere = false;
    }
  };

  onTouchStart = (e) => {
    if (!e.touches?.length) return;
    const touch = e.touches[0];
    if (!this.isInsideClientPoint(touch.clientX, touch.clientY)) return;

    this.lastTouchAt = Date.now();
    this.isMouseDown = true;
    this.downStartedHere = true;
    this.dragging = false;
    this._strokeDirty = false;
    this.dragStart.x = touch.clientX;
    this.dragStart.y = touch.clientY;
  };

  onTouchMove = (e) => {
    if (!e.touches?.length) return;
    const touch = e.touches[0];
    this.lastTouchAt = Date.now();

    if (this.isInsideClientPoint(touch.clientX, touch.clientY)) {
      const { x, y } = this.getClientCanvasPixel(touch.clientX, touch.clientY);
      this.setState({
        mouseX: x >= 0 && x < this.virtualWidth ? x + 1 : this.state.mouseX,
        mouseY: y >= 0 && y < this.virtualHeight ? y + 1 : this.state.mouseY,
      });
    }

    if (!this.isMouseDown) return;

    e.preventDefault();
    const dxClient = touch.clientX - this.dragStart.x;
    const dyClient = touch.clientY - this.dragStart.y;
    if (!this.dragging) {
      const moved2 = dxClient * dxClient + dyClient * dyClient;
      if (moved2 < this.DRAG_THRESHOLD * this.DRAG_THRESHOLD) return;
      this.dragging = true;
      this.viewportDirty = true;
    }

    const canvas = this.canvasRef.current;
    const dx = dxClient / this.scale;
    const dy = dyClient / this.scale;

    this.offsetX = Math.min(
      Math.max(this.offsetX - dx, 0),
      this.virtualWidth - canvas.width / this.scale
    );
    this.offsetY = Math.min(
      Math.max(this.offsetY - dy, 0),
      this.virtualHeight - canvas.height / this.scale
    );

    this.dragStart.x = touch.clientX;
    this.dragStart.y = touch.clientY;
    this.draw();
  };

  onTouchEnd = async (e) => {
    this.lastTouchAt = Date.now();
    if (!this.downStartedHere) return;

    const touch = e.changedTouches?.[0];
    const wasDragging = this.dragging;
    const inside = touch ? this.isInsideClientPoint(touch.clientX, touch.clientY) : false;

    this.isMouseDown = false;
    this.dragging = false;
    this.downStartedHere = false;

    if (wasDragging || !inside || !touch) return;

    const { x, y } = this.getClientCanvasPixel(touch.clientX, touch.clientY);
    if (x < 0 || y < 0 || x >= this.virtualWidth || y >= this.virtualHeight) return;
    await this.handleCanvasSelection(x, y);
  };

  onTouchCancel = () => {
    this.lastTouchAt = Date.now();
    this.isMouseDown = false;
    this.dragging = false;
    this.downStartedHere = false;
  };

  onWheel = (e) => {
    e.preventDefault();
    const canvas = this.canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;
    const zoomX = mx / this.scale + this.offsetX;
    const zoomY = my / this.scale + this.offsetY;

    this.viewportDirty = true;
    const delta = e.deltaY < 0 ? 1.2 : 0.8;
    const visibleWidth  = canvas.width;
    const visibleHeight = canvas.height;
    const minVisibleScale = Math.max(visibleWidth / this.virtualWidth, visibleHeight / this.virtualHeight);
    const newScale = Math.min(this.maxScale, Math.max(minVisibleScale, this.scale * delta));
    this.scale = newScale;

    this.offsetX = Math.min(Math.max(zoomX - mx / newScale, 0), this.virtualWidth  - canvas.width  / newScale);
    this.offsetY = Math.min(Math.max(zoomY - my / newScale, 0), this.virtualHeight - canvas.height / newScale);
    this.draw();
  };

  onKeyDown = (e) => {
    // Ignore when typing in inputs/textareas
    const tag = (e.target?.tagName || '').toLowerCase();
    if (tag === 'input' || tag === 'textarea' || e.target?.isContentEditable) return;
    if (e.code === 'Space' || e.key === ' ' || e.key === 'Spacebar') {
      e.preventDefault(); // stop page from scrolling
      this.isSpaceDown = true;
    }
  };

  onKeyUp = (e) => {
    if (e.code === 'Space' || e.key === ' ' || e.key === 'Spacebar') {
      this.isSpaceDown = false;
    }
  };

  onContextMenu = (e) => e.preventDefault();

  // -------------------- zoom buttons --------------------
  adjustZoom = (delta) => {
    this.viewportDirty = true;
    const canvas = this.canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const mx = rect.width / 2;
    const my = rect.height / 2;
    const zoomX = mx / this.scale + this.offsetX;
    const zoomY = my / this.scale + this.offsetY;

    const visibleWidth  = canvas.width;
    const visibleHeight = canvas.height;
    const minVisibleScale = Math.max(visibleWidth / this.virtualWidth, visibleHeight / this.virtualHeight);
    const newScale = Math.min(this.maxScale, Math.max(minVisibleScale, this.scale * delta));
    this.scale = newScale;

    this.offsetX = Math.min(Math.max(zoomX - mx / newScale, 0), this.virtualWidth  - canvas.width  / newScale);
    this.offsetY = Math.min(Math.max(zoomY - my / newScale, 0), this.virtualHeight - canvas.height / newScale);

    this.draw();
  };

  setZoom = (targetScale) => {
    const canvas = this.canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const mx = rect.width / 2;
    const my = rect.height / 2;

    const zoomX = mx / this.scale + this.offsetX;
    const zoomY = my / this.scale + this.offsetY;

    const newScale = Math.min(this.maxScale, Math.max(this.minScale, targetScale));
    this.scale = newScale;

    this.offsetX = Math.min(Math.max(zoomX - mx / newScale, 0), this.virtualWidth  - canvas.width  / newScale);
    this.offsetY = Math.min(Math.max(zoomY - my / newScale, 0), this.virtualHeight - canvas.height / newScale);

    this.draw();
  };

  toggleFullScreen = () => {
    this.viewportDirty = false;
    this.setState((prev) => ({ isFullScreen: !prev.isFullScreen }), this.resizeCanvas);
  };

  handleOwnedToggle = async (on) => {
    if (on) {
      await this.preloadOwnedArtworkMasks();
      this.setState({ showOwnedArtwork: true }, this.draw);
    } else {
      this.setState({ showOwnedArtwork: false, ownedMasks: [] }, this.draw);
    }
  };
  // ---------------- brush batch mode ----------------
  // add (without recalculating cost) a single pixel to batch set
  addToBatchSet = (x, y) => {
    if (x < 0 || y < 0 || x >= this.virtualWidth || y >= this.virtualHeight) return false;
    if (this.state.batchSet.size >= this.BATCH_LINK_LIMIT) return false;
    const key = this.pixelKey(x, y);
    if (this.state.batchSet.has(key)) return false;
    this.state.batchSet.add(key);
    return true;
  };

  // paint a filled circle of radius = this.state.brushSize at (x, y)
  addBrushAt = (x, y) => {
    const r = Math.max(1, Number(this.state.brushSize || 1));
    if (r === 1) {
      return this.addToBatchSet(x, y);
    }
    let added = false;
    for (let dy = -r; dy <= r; dy++) {
      for (let dx = -r; dx <= r; dx++) {
        if (dx*dx + dy*dy <= r*r) {
          added = this.addToBatchSet(x + dx, y + dy) || added;
        }
      }
    }
    return added;
  };

  // interpolate from (x0,y0) -> (x1,y1) and stamp the brush along the path
  addBrushStroke = (x0, y0, x1, y1) => {
    const dx = x1 - x0;
    const dy = y1 - y0;
    const steps = Math.max(Math.abs(dx), Math.abs(dy));
    if (steps === 0) return this.addBrushAt(x0, y0);
    let any = false;
    for (let s = 0; s <= steps; s++) {
      const x = Math.round(x0 + (dx * s) / steps);
      const y = Math.round(y0 + (dy * s) / steps);
      any = this.addBrushAt(x, y) || any;
    }
    return any;
  };


  // -------------------- draw --------------------
  worldHasBit(maskMap, x0, y0) {
    const col1 = x0 + 1, row1 = y0 + 1; // to 1-based
    const cx = Math.floor((col1 - 1) / CHUNK);
    const cy = Math.floor((row1 - 1) / CHUNK);
    const key = `${cy},${cx}`;
    const ch = maskMap.get(key);
    if (!ch) return false;
    const i = col1 - ch.origin.col1;
    const j = row1 - ch.origin.row1;
    if (i < 0 || j < 0 || i >= ch.width || j >= ch.height) return false;
    return ch.bits[j * ch.width + i] === 1;
  }

  draw = () => {
    const canvas = this.canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    ctx.setTransform(this.scale, 0, 0, this.scale, -this.offsetX * this.scale, -this.offsetY * this.scale);
    ctx.imageSmoothingEnabled = false;

    // base image
    ctx.drawImage(this.offscreenCanvas, 0, 0);
    

    // debug overlays (grid, borders, holes)
    drawDebug(ctx, {
      canvasPixelWidth: this.virtualWidth,
      canvasPixelHeight: this.virtualHeight,
      zoom: this.scale,
      showGrid: this.state.showGrid,
      showBorders: this.state.showBorders,
      showHoles: this.state.showHoles,
      chunk: CHUNK,
    });

    // highlight current artwork silhouette
    const { highlightedMask, highlightColor } = this.state;
    if (highlightedMask) {
      const { chunks, map } = highlightedMask;
      const fillColor = highlightColor || '#00FFFF';

      ctx.save();

      // fill masked bits
      ctx.globalAlpha = 0.25;
      ctx.fillStyle = fillColor;
      for (const ch of chunks) {
        const { origin, width, height, bits } = ch;
        const baseX = origin.col1 - 1;
        const baseY = origin.row1 - 1;
        for (let j = 0; j < height; j++) {
          const rowOff = j * width;
          for (let i = 0; i < width; i++) {
            if (!bits[rowOff + i]) continue;
            ctx.fillRect(baseX + i, baseY + j, 1, 1);
          }
        }
      }

      // outline edges
      ctx.globalAlpha = 0.9;
      ctx.strokeStyle = fillColor;
      ctx.lineWidth = 1 / this.scale;
      const has = (x0, y0) => this.worldHasBit(map, x0, y0);
      for (const ch of chunks) {
        const { origin, width, height, bits } = ch;
        const baseX = origin.col1 - 1;
        const baseY = origin.row1 - 1;
        for (let j = 0; j < height; j++) {
          const rowOff = j * width;
          for (let i = 0; i < width; i++) {
            if (!bits[rowOff + i]) continue;
            const x = baseX + i, y = baseY + j;
            if (!has(x - 1, y) || !has(x + 1, y) || !has(x, y - 1) || !has(x, y + 1)) {
              ctx.strokeRect(x, y, 1, 1);
            }
          }
        }
      }
      ctx.restore();
    }

    // --- Owned artworks overlay ---
    if (this.state.showOwnedArtwork && this.state.ownedMasks?.length) {
      const OVERLAY = '#f59e0b'; // amber
      const ctx2 = this.canvasRef.current.getContext('2d');
      ctx2.save();
      ctx2.globalAlpha = 0.25;
      ctx2.fillStyle = OVERLAY;

      for (const m of this.state.ownedMasks) {
        for (const ch of m.chunks) {
          const { origin, width, height, bits } = ch;
          const baseX = origin.col1 - 1;
          const baseY = origin.row1 - 1;
          for (let j = 0; j < height; j++) {
            const rowOff = j * width;
            for (let i = 0; i < width; i++) {
              if (!bits[rowOff + i]) continue;
              ctx2.fillRect(baseX + i, baseY + j, 1, 1);
            }
          }
        }
      }

      // optional outline
      ctx2.globalAlpha = 0.9;
      ctx2.strokeStyle = OVERLAY;
      ctx2.lineWidth = 1 / this.scale;
      const has = (maskMap, x0, y0) => this.worldHasBit(maskMap, x0, y0);

      for (const m of this.state.ownedMasks) {
        const { chunks, map } = m;
        for (const ch of chunks) {
          const { origin, width, height, bits } = ch;
          const baseX = origin.col1 - 1;
          const baseY = origin.row1 - 1;
          for (let j = 0; j < height; j++) {
            const rowOff = j * width;
            for (let i = 0; i < width; i++) {
              if (!bits[rowOff + i]) continue;
              const x = baseX + i, y = baseY + j;
              if (!has(map, x-1,y) || !has(map, x+1,y) || !has(map, x,y-1) || !has(map, x,y+1)) {
                ctx2.strokeRect(x, y, 1, 1);
              }
            }
          }
        }
      }
      ctx2.restore();
    }

    // --- Batch selection overlay ---
    if (this.state.batchMode && this.state.batchSet && this.state.batchSet.size > 0) {
      const ctx = this.canvasRef.current.getContext('2d');
      for (const key of this.state.batchSet) {
        const [x, y] = key.split(',').map(Number);
        ctx.save();
        ctx.globalAlpha = 0.35;
        ctx.fillStyle = '#22c55e';        // green for "to buy"
        ctx.fillRect(x, y, 1, 1);
        ctx.globalAlpha = 0.85;
        ctx.strokeStyle = '#22c55e';
        ctx.lineWidth = 1 / this.scale;
        // outline only exposed edges
        //const neighbors = [[x-1,y],[x+1,y],[x,y-1],[x,y+1]];
        const has = (nx, ny) => this.state.batchSet.has(this.pixelKey(nx, ny));
        if (!has(x-1, y)) ctx.strokeRect(x, y, 1, 1);
        else if (!has(x+1, y)) ctx.strokeRect(x, y, 1, 1);
        else if (!has(x, y-1)) ctx.strokeRect(x, y, 1, 1);
        else if (!has(x, y+1)) ctx.strokeRect(x, y, 1, 1);
        ctx.restore();
      }
    }

    const sp = this.state.selectedPixel;
    if(sp) {
      this.drawSelectionCorners(ctx, sp.x, sp.y);
    }
  };

  // -------------------- Pixel Buy --------------------
  // Unique key for a pixel (0-based)
  pixelKey = (x, y) => `${x},${y}`;
  
  // Convert selection Set -> [{row,column}] (1-based) for backend
  getBatchArray = () => {
    return Array.from(this.state.batchSet).map(s => {
      const [x, y] = s.split(',').map(Number);
      return { row: y + 1, column: x + 1 };
    });
  };

  // Live cost
  estimateBatchCost = (batchSet, hours) => {
    const price = this.props.backendContext.canvas?.price ?? this.PRICE_PER_PIXEL_PER_HOUR;
    if (!price) return 0;

    // base: just the new pixels
    let total = (batchSet?.size || 0) * price;

    return Math.round(total); // integer tokens, if you want
  }

  // Enter batch mode preselecting a pixel
  startBatchModeFromPixel = async (x, y) => {
    if (!this.props.backendContext.userProfile) {
      this.setState({ logInRequired: true});
      return;
    }
    // make sure we have owned masks available for adjacency
    await this.preloadOwnedArtworkMasks();

    const next = new Set([this.pixelKey(x, y)]);
    const hours = this.state.batchHours || 24;
    const cost = await this.estimateBatchCost(next, hours);

    this.setState({
      batchMode: true,
      batchSet: next,
      batchHours: hours,
      batchCost: cost,
      showBuyBatchPanel: true,
    }, this.draw);
  };

  // Exit batch mode
  exitBatchMode = () => {
    this.setState({
      batchMode: false,
      batchSet: new Set(),
      showBuyBatchPanel: false,
    }, this.draw);
  };

  // Toggle a pixel in batch selection
  toggleBatchPixel = async (x, y) => {
    const key = this.pixelKey(x, y);
    const next = new Set(this.state.batchSet);
    if (next.has(key)) next.delete(key);
    else {
      if (next.size >= this.BATCH_LINK_LIMIT) return; // silently ignore if over limit
      next.add(key);
    }
    const cost = await this.estimateBatchCost(next, this.state.batchHours);
    this.setState({ batchSet: next, batchCost: cost }, this.draw);
  };

  // Confirm buy (batch)
  confirmBuyBatch = async () => {
    const selections = this.getBatchArray();
    if (!selections.length) return this.exitBatchMode();
    // Open the result modal immediately in loading state
    this.setState({
      isBuying: true,               // keeps your Confirm button spinner
      showBatchResultModal: true,   // open modal
      batchResultLoading: true,
      batchResultError: null,
      batchResult: null
    });

    try {
      const res = await this.props.backendContext.buyPixelBatch(
        this.props.page,
        selections,
        this.state.batchHours
      );
      // Optionally highlight the first affected artwork
      const affected = Array.isArray(res?.affectedArtworks) ? res.affectedArtworks : [];
      if (affected.length) await this.highlightArtwork(affected[0]);

      this.setState({
        batchResult: {
          purchased: res?.purchased ?? [],
          skipped: res?.skipped ?? [],
          affectedArtworks: affected,
          cost: res?.cost ?? 0,
        },
        batchResultLoading: false,
        selectedPixel: null, 
        selectedPixelStatus: null 
      });
    } catch (e) {
      console.error('buyPixelBatch failed:', e);
      this.setState({
        batchResultError: e?.message || 'Unknown error',
        batchResultLoading: false,
      });
    } finally {
      this.exitBatchMode();
      this.setState({ isBuying: false });
    }
  };

  // Confirm buy
  confirmBuyPixel = async (hours) => {
    const sp = this.state.selectedPixel;
    if (!sp) return this.setState({ buyModalOpen: false });

    try {
      // call your batch lambda with a single selection
      const selections = [{ row: sp.y + 1, column: sp.x + 1 }];
      const res = await this.props.backendContext.buyPixelBatch(this.props.page, selections, hours);

      // Optionally refresh silhouette if the active artwork changed/created
      const affected = Array.isArray(res?.affectedArtworks) ? res.affectedArtworks : [];
      if (affected.length) {
        // prefer to highlight the first affected artwork
        await this.highlightArtwork(affected[0]);
      }

      // Re-check pixel status after purchase (so panel updates)
      const status = await resolvePixelOccupancy(this.props.backendContext, this.props.page, sp.x, sp.y);
      this.setState({ selectedPixelStatus: status });
    } catch (e) {
      console.error('buyPixelBatch failed:', e);
    } finally {
      this.setState({ buyModalOpen: false });
    }
  };

  //InfoPanel Countdown
  startCountdown = (expiresAt) => {
    this.stopCountdown();
    if (!expiresAt) return;
  
    // If expiresAt is an ISO string (e.g., "2025-09-19T23:59:59Z"), this is correct.
    // If it's epoch *seconds*, convert with: Number(expiresAt) * 1000
    const ms = new Date(expiresAt).getTime();
    if (!Number.isFinite(ms)) return;
  
    this._expiresAtMs = ms;
    this.updateCountdown(); // set initial value immediately
    this._countdownInterval = setInterval(this.updateCountdown, 1000);
  };
  
  stopCountdown = () => {
    if (this._countdownInterval) {
      clearInterval(this._countdownInterval);
      this._countdownInterval = null;
    }
    this._expiresAtMs = null;
  };
  
  updateCountdown = () => {
    if (!this._expiresAtMs) return;
  
    const diff = Math.max(0, this._expiresAtMs - Date.now());
    const hours = String(Math.floor(diff / 1000 / 3600)).padStart(2, '0');
    const minutes = String(Math.floor((diff / 1000 / 60) % 60)).padStart(2, '0');
    const seconds = String(Math.floor((diff / 1000) % 60)).padStart(2, '0');
  
    const next = `${hours}:${minutes}:${seconds}`;
    if (diff === 0) {
      // Hit zero → freeze and stop ticking
      this.setState({ timeRemaining: '00:00:00' });
      this.stopCountdown();
    } else {
      // Avoid extra renders if the text hasn't changed
      if (this.state.timeRemaining !== next) {
        this.setState({ timeRemaining: next });
      }
    }
  };
  // -------------------- UI --------------------
  renderInfoPanel() {
    const { selectedPixel, selectedPixelStatus } = this.state;
    if (!selectedPixel) return null;

    const x1 = selectedPixel.x + 1;
    const y1 = selectedPixel.y + 1;

    const status = selectedPixelStatus?.status ?? 'loading';
    const ownedBy = selectedPixelStatus?.artwork?.owner;
    const ownedArtworkId = selectedPixelStatus?.artwork?.id;
    const artworkTitle = selectedPixelStatus?.artwork?.title;

    const statusLabel =
      status === 'loading' ? 'Checking…' :
      status === 'empty' ? 'Empty' :
      status === 'owned' ? 'Owned' :
      status === 'expiredOwner' ? 'Expired' :
      String(status);

    const expiresLabel =
      status === 'expiredOwner' ? 'Expired' :
      status === 'owned'
        ? (this.state.timeRemaining ?? '—')
        : '—';

    return (
      <div className="fixed inset-x-0 bottom-4 z-[900] pointer-events-none">
        <div
          className="
            pointer-events-auto mx-auto
            w-[min(92vw,720px)]
            rounded-2xl border border-slate-700
            bg-slate-900/90 backdrop-blur shadow-2xl p-3
          "
        >
          {/* Row 1: coords (left) • status (center, nudged left) • close (right) */}
          <div className="flex items-center justify-between gap-2">
            <div className="text-sm opacity-80 whitespace-nowrap">
              Pixel: ({x1}, {y1})
            </div>

            <div className="flex-1 text-center">
              {/* inline-block so transform works; tweak the translate to taste */}
              <span className="inline-block font-semibold -translate-x-7">
                {statusLabel}
              </span>
            </div>

            <button
              className="px-2 py-1 text-zinc-400 hover:text-white"
              onClick={() => this.setState({ selectedPixel: null, selectedPixelStatus: null })}
              aria-label="Close"
              title="Close"
            >
              <X />
            </button>
          </div>

          {/* Row 2: stacked labels */}
          <div className="mt-3 grid grid-cols-3 gap-3 text-white">
            {/* Owner */}
            <div className="flex flex-col truncate">
              <span className="text-[11px] leading-4 uppercase tracking-wide text-zinc-400">
                Owner
              </span>
              <span className="font-medium truncate">{ownedBy ?? '—'}</span>
            </div>

            {/* Title */}
            <div className="flex flex-col items-center text-center truncate">
              <span className="text-[11px] leading-4 uppercase tracking-wide text-zinc-400">
                Title
              </span>
              <span className="font-medium truncate">{artworkTitle ?? '—'}</span>
            </div>

            {/* Expires */}
            <div className="flex flex-col items-end text-right truncate">
              <span className="text-[11px] leading-4 uppercase tracking-wide text-zinc-400">
                Expires
              </span>
              <span className="font-mono truncate">{expiresLabel}</span>
            </div>
          </div>

          {/* Row 3: Buy (left) • Show Artwork (right) */}
          <div className="mt-3 flex items-center justify-between gap-2">
            <button
              className="text-xs px-3 py-1 rounded border border-slate-600 hover:bg-slate-800"
              onClick={() => this.startBatchModeFromPixel(selectedPixel.x, selectedPixel.y)}
            >
              Buy pixel
            </button>

            {(status === 'owned' || status === 'expiredOwner') && ownedArtworkId ? (
              <button
                className="text-xs px-3 py-1 rounded border border-slate-600 hover:bg-slate-800"
                onClick={() => this.openArtworkModal(ownedArtworkId)}
              >
                Show Artwork
              </button>
            ) : (
              <div className="text-xs px-3 py-1 rounded opacity-0 select-none">placeholder</div>
            )}
          </div>
        </div>
      </div>
    );
  }

  renderBuyBatchPanel() {
    if (!this.state.showBuyBatchPanel) return null;

    const selected = this.state.batchSet.size;
    const max = this.BATCH_LINK_LIMIT;
    const hours = this.state.batchHours;
    const cost = this.estimateBatchCost(this.state.batchSet, hours);

    return (
      <div className="fixed left-0 right-0 bottom-0 z-[950]">
        <div className="mx-auto max-w-4xl bg-slate-900/95 text-white border-t border-slate-700 rounded-t-xl p-3 shadow-2xl">
          <div className="flex items-center justify-between">
            {/* Left: Exit */}
            <button
              className="px-2 py-1 text-zinc-400 hover:text-white"
              onClick={this.exitBatchMode}
              aria-label="Exit buy mode"
              title="Exit buy mode"
            >
              <X />
            </button>

            {/* Center: Title */}
            <div className="text-sm opacity-90">
              Buy Pixels — Selected <span className="font-mono">{selected}</span>/<span className="font-mono">{max}</span>
            </div>

            {/* Right: Confirm */}
            <Button
              className={`text-sm px-3 py-1 rounded ${selected ? 'bg-indigo-600' : 'bg-slate-700 cursor-not-allowed'}`}
              onPress={this.confirmBuyBatch}
              disabled={!selected}
              isLoading={this.state.isBuying}
            >
              Buy Pixels
            </Button>
          </div>

          <div className="mt-3 grid grid-cols-3 gap-3 items-end">
            <div>
              <div className="text-xs opacity-70 mb-1">Selected</div>
              <div className="text-lg font-mono">{selected} / {max}</div>
            </div>

            <div>
              <div className="text-xs opacity-70 mb-1">Hours</div>
              <div className="text-lg font-mono">1</div>
            </div>

            <div>
              <div className="text-xs opacity-70 mb-1">Cost</div>
              <div className="text-lg font-mono">{formatInt(cost)}</div>
            </div>
          </div>

          <div className="mt-3 text-xs opacity-70">
            Tip: while in buy mode, click to add/remove pixels. Hold <kbd>Space</kbd> and drag to paint with a brush.
          </div>
        </div>
      </div>
    );
  }

  renderBatchResultModal() {
    const {
      showBatchResultModal,
      batchResultLoading,
      batchResultError,
      batchResult,
    } = this.state;

    return (
      <Modal
        isOpen={showBatchResultModal}
        onClose={() => this.setState({ showBatchResultModal: false })}
        backdrop="blur"
        classNames={{ base: "bg-slate-900 text-white" }}
      >
        <ModalContent>
          <ModalHeader className="text-xl font-semibold">
            {batchResultLoading
              ? "Purchasing pixels…"
              : batchResultError
              ? "Purchase failed"
              : "Purchase summary"}
          </ModalHeader>

          <ModalBody>
            {batchResultLoading && (
              <div className="flex flex-col items-center gap-3 py-6">
                <Spinner size="lg" />
                <div className="text-sm text-gray-300">
                  Calling the backend and reserving your pixels…
                </div>
              </div>
            )}

            {!batchResultLoading && batchResultError && (
              <div className="space-y-3">
                <p className="text-red-400 font-medium">Something went wrong</p>
                <pre className="bg-gray-800 p-3 rounded-xl overflow-auto text-sm">
                  {batchResultError}
                </pre>
              </div>
            )}

            {!batchResultLoading && !batchResultError && batchResult && (
              <div className="space-y-6">
                <div className="flex flex-wrap gap-3 text-sm">
                  <div className="px-2 py-1 rounded bg-emerald-700/40 border border-emerald-500/30">
                    Purchased: <b>{batchResult.purchased.length}</b>
                  </div>
                  <div className="px-2 py-1 rounded bg-amber-700/40 border border-amber-500/30">
                    Skipped: <b>{batchResult.skipped.length}</b>
                  </div>
                  <div className="px-2 py-1 rounded bg-indigo-700/40 border border-indigo-500/30">
                    Cost: <b>{batchResult.cost}</b>
                  </div>
                  {batchResult.affectedArtworks?.length ? (
                    <div className="px-2 py-1 rounded bg-cyan-700/40 border border-cyan-500/30">
                      Affected artworks: <b>{batchResult.affectedArtworks.length}</b>
                    </div>
                  ) : null}
                </div>

                {/* Purchased list */}
                <div>
                  <h3 className="mb-2 font-semibold">Purchased</h3>
                  <div className="max-h-64 overflow-auto rounded border border-slate-700">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-800/70">
                        <tr>
                          <th className="text-left px-3 py-2">Row</th>
                          <th className="text-left px-3 py-2">Column</th>
                          <th className="text-left px-3 py-2">Color</th>
                        </tr>
                      </thead>
                      <tbody>
                        {batchResult.purchased.length === 0 ? (
                          <tr>
                            <td className="px-3 py-2 text-gray-400" colSpan={3}>None</td>
                          </tr>
                        ) : (
                          batchResult.purchased.map((p, i) => (
                            <tr key={`p-${i}`} className="odd:bg-slate-800/30">
                              <td className="px-3 py-1">{p.row}</td>
                              <td className="px-3 py-1">{p.column}</td>
                              <td className="px-3 py-1">{p.color ?? '—'}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Skipped list */}
                <div>
                  <h3 className="mb-2 font-semibold">Skipped</h3>
                  <div className="max-h-64 overflow-auto rounded border border-slate-700">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-800/70">
                        <tr>
                          <th className="text-left px-3 py-2">Row</th>
                          <th className="text-left px-3 py-2">Column</th>
                          <th className="text-left px-3 py-2">Reason</th>
                        </tr>
                      </thead>
                      <tbody>
                        {batchResult.skipped.length === 0 ? (
                          <tr>
                            <td className="px-3 py-2 text-gray-400" colSpan={3}>None</td>
                          </tr>
                        ) : (
                          batchResult.skipped.map((s, i) => (
                            <tr key={`s-${i}`} className="odd:bg-slate-800/30">
                              <td className="px-3 py-1">{s.row}</td>
                              <td className="px-3 py-1">{s.column}</td>
                              <td className="px-3 py-1">{s.reason}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </ModalBody>

          <ModalFooter>
            <Button
              color={batchResultError ? "danger" : "primary"}
              onPress={() => this.setState({ showBatchResultModal: false })}
              isDisabled={batchResultLoading}
            >
              {batchResultLoading ? "Working…" : "Close"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    );
  }

  render() {
    const { mouseX, mouseY, isFullScreen } = this.state;
    const { isLoading } = this.props.backendContext;

    return (
      <div className="w-full h-full relative overflow-hidden">
        {/* Coordinate display */}
        <div className="absolute top-12 left-1 text-white px-3 py-1 rounded text-sm font-mono z-[800]">
          <div className="text-center bg-slate-900 rounded-md">
            {mouseX !== null && mouseY !== null ? `(${mouseX}, ${mouseY})` : '(-, -)'}
          </div>
        </div>

        {/* Zoom Controls */}
        <div className={`absolute bottom-8 left-0 z-[800] group transition-all duration-300 ${
          this.state.isFullScreen ? 'opacity-50 translate-x-[-85%] hover:translate-x-0 hover:opacity-100'
                                  : 'opacity-100 translate-x-0'
        }`}>
          <div className="flex flex-col items-center space-y-3 bg-black bg-opacity-60 p-2 rounded-r-lg">
            <label htmlFor="ownerArtwork-toggle" className="text-sm">
              Owned
            </label>
            <Switch
              id="ownerArtwork-toggle"
              isSelected={this.state.showOwnedArtwork}
              onChange={(e) => this.handleOwnedToggle(e.target.checked)}
              size="sm"
              color="secondary"
              classNames={{
                base: "text-white",
                wrapper: "bg-indigo-700/50 data-[selected=true]:bg-indigo-600",
              }}
            />
            <Button onPress={() => this.adjustZoom(1.2)} isIconOnly className="bg-indigo-600 text-white w-10 h-10 text-xl font-bold rounded-full shadow hover:bg-purple-500">+</Button>
            <Button onPress={() => this.adjustZoom(0.8)} isIconOnly className="bg-indigo-600 text-white w-10 h-10 text-xl font-bold rounded-full shadow hover:bg-purple-500">−</Button>
            <Button onPress={this.toggleFullScreen} isIconOnly className="bg-indigo-600 text-white w-10 h-10 text-xl font-bold rounded-full shadow hover:bg-purple-500">
              <Expand />
            </Button>
            <TutorialModal />
          </div>
        </div>

        {/* Canvas */}
        <div
          ref={this.canvasContainerRef}
          className={`${isFullScreen ? 'fixed inset-0 z-[700]' : 'w-[90vw] h-[90vh] mx-auto mt-10'} flex items-center justify-center border border-gray-300 bg-black`}
        >
          {isLoading ? (
            <h2 className="text-center text-xl">Loading</h2>
          ) : (
            <canvas
              ref={this.canvasRef}
              className={`${this.dragging ? 'cursor-grabbing' : 'cursor-crosshair'}`}
              style={{ imageRendering: 'pixelated', display: 'block', touchAction: 'none' }}
            />
          )}
        </div>

        <BuyPixelModal
          isOpen={this.state.buyModalOpen}
          onClose={() => this.setState({ buyModalOpen: false })}
          onConfirm={this.confirmBuyPixel}
        />

        {/* Owner modal */}
        {this.state.showOwnerModal && (
          <ArtworkOwnerModal
            artworkId={this.state.ownerModalArtworkId}
            onCloseModal={() => {
              this.setState({ showOwnerModal: false, ownerModalArtworkId: null });
              this.props.onClearArtwork?.();
            }}
            onRequestSeed={this.requestArtworkSeed}
            onApplyPatch={this.applyPatchFromModal}
          />
        )}

        {/* Guest modal */}
        {this.state.showGuestModal && (
          <ArtworkGuestModal
            artworkId={this.state.guestModalArtworkId}
            onCloseModal={() => {
              this.setState({ showGuestModal: false, guestModalArtworkId: null });
              this.props.onClearArtwork?.();
            }}
          />
        )}

        {this.renderInfoPanel()}
        {this.renderBuyBatchPanel()}
        {this.renderBatchResultModal()}

        <LogInRequiredModal
          isOpen={this.state.logInRequired}
          onClose={() => this.setState({ logInRequired: false })}
          message="You must be logged in to own pixels."
        />
      </div>
    );
  }
}

export default ArtworkCanvas;
























